import { ORCHESTRATOR_ENDPOINTS } from "common/endpoints";
import { request } from "common/services/request";

import type { ConversationShares } from "./types";

/** Fetch the link token + existing grants for a conversation (owner only). */
export const getShares = (conversationId: string) =>
  request("orchestrator").get<ConversationShares>(
    ORCHESTRATOR_ENDPOINTS.CONVERSATIONS.SHARES(conversationId)
  );

/** Grant a user (by email, any organisation) read access to the conversation. */
export const addGrant = (conversationId: string, email: string) =>
  request("orchestrator").post(
    ORCHESTRATOR_ENDPOINTS.CONVERSATIONS.SHARES(conversationId),
    { email }
  );

/** Revoke a link share or a direct grant. */
export const revokeShare = (conversationId: string, shareId: string) =>
  request("orchestrator").delete(
    ORCHESTRATOR_ENDPOINTS.CONVERSATIONS.SHARE_BY_ID(conversationId, shareId)
  );

/**
 * Load a conversation shared with the caller, by id. Backend read access is
 * mode- and grant-aware, so an "anyone" viewer or a grantee gets a read-only view.
 */
export const getSharedConversationById = (conversationId: string) =>
  request("orchestrator").get(
    ORCHESTRATOR_ENDPOINTS.CONVERSATIONS.BY_ID(conversationId),
    { params: { include_messages: true } }
  );

/** List conversations that other users have shared directly with the caller. */
export const getSharedWithMe = () =>
  request("orchestrator").get(
    ORCHESTRATOR_ENDPOINTS.CONVERSATIONS.SHARED_WITH_ME
  );

/**
 * Build the absolute link a recipient opens (requires them to be logged in).
 * There is no token — access is governed server-side by the conversation's
 * share mode and grants; the URL just names the conversation.
 */
export const shareLinkForConversation = (conversationId: string) =>
  `${window.location.origin}/shared-with/${conversationId}`;

// ---------------------------------------------------------------------------
// Rich share-dialog API (ShareState). Mirrors the shape the dialog expects and
// maps it onto the conversation share endpoints.
// ---------------------------------------------------------------------------

export type ShareAccessMode = "restricted" | "anyone";

export interface SharePerson {
  email: string;
  full_name: string | null;
  avatar_url: string | null;
  removable: boolean;
  grant_id: string | null;
  access_level: string;
}

export interface ShareState {
  access_mode: ShareAccessMode;
  people: SharePerson[];
}

export interface PersonLookupResult {
  email: string;
  full_name: string | null;
  avatar_url: string | null;
  exists: boolean;
}

/** Full sharing state for the dialog (link token, access mode, people). */
export const getShare = (conversationId: string): Promise<ShareState> =>
  request("orchestrator")
    .get<ShareState>(ORCHESTRATOR_ENDPOINTS.CONVERSATIONS.SHARE_STATE(conversationId))
    .then((res) => res.data);

/** Resolve an email for the "Add people" typeahead. */
export const lookupSharePerson = (
  conversationId: string,
  email: string
): Promise<PersonLookupResult> =>
  request("orchestrator")
    .get<PersonLookupResult>(
      ORCHESTRATOR_ENDPOINTS.CONVERSATIONS.SHARE_LOOKUP(conversationId),
      { params: { email } }
    )
    .then((res) => res.data);

/** Grant a person access by email, then return the refreshed ShareState. */
export const addSharePerson = (
  conversationId: string,
  email: string,
  notify: boolean
): Promise<ShareState> =>
  request("orchestrator")
    .post(ORCHESTRATOR_ENDPOINTS.CONVERSATIONS.SHARES(conversationId), { email, notify })
    .then(() => getShare(conversationId));

/** Revoke a grant, then return the refreshed ShareState. */
export const removeSharePerson = (
  conversationId: string,
  grantId: string
): Promise<ShareState> =>
  request("orchestrator")
    .delete(ORCHESTRATOR_ENDPOINTS.CONVERSATIONS.SHARE_BY_ID(conversationId, grantId))
    .then(() => getShare(conversationId));

/** Switch between "restricted" and "anyone with the link". */
export const setShareMode = (
  conversationId: string,
  mode: ShareAccessMode
): Promise<ShareState> =>
  request("orchestrator")
    .put<ShareState>(ORCHESTRATOR_ENDPOINTS.CONVERSATIONS.SHARE_MODE(conversationId), { mode })
    .then((res) => res.data);
